import { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Divider, Modal } from "@mui/material";
import {
  ModalTabPanel,
  StyledTab,
  StyledTabs,
  a11yProps,
} from "../Common/UI/TabPanel";
import {
  CustomModalHeader,
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
} from "../Common/styles/modal";
import { AxiosResponse } from "axios";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { checkIfContentEmpty } from "../../utils/tiptap";
import { DrawerForm } from "../Common/styles/drawer";
import InvitationTranslation from "./Modals/InvitationTranslation";
import RemindersTranslation from "./Modals/RemindersTranslation";

let schema = yup.object().shape({
  invitationSubject: yup.string().required("Email Subject is Required"),
  invitationBody: yup
    .string()
    .test("valid-html", "Message Body is required", (value) => {
      return !checkIfContentEmpty(value || "");
    }),
  isReminder: yup.boolean().optional(),
  emailSubject: yup.string().when("isReminder", ([isReminder], schema) => {
    return isReminder === true
      ? schema.required("Email Subject is required")
      : schema.optional();
  }),
  emailBody: yup.string().when("isReminder", ([isReminder], schema) => {
    return isReminder === true
      ? schema.test("valid-html", "Message Body is required", (value) => {
          return !checkIfContentEmpty(value || "");
        })
      : schema.optional();
  }),
});
const SurveyPackagTranslationModal = ({
  showModal,
  closeModal,
  data,
  studyId,
  preview,
  lang,
}: any) => {
  const [type, setType] = useState<string>("invitation");
  const [submitLoader, setSubmitLoader] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [langData, setLangData] = useState<any>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // const res = await http.get(`/admin/study/${studyId}/languages`);
        // setLangData(res.data.data);
        setLangData({});
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
      }
    };
    fetchData();
  }, [data.id, setLoading]);

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      const body: any = {
        invitationSubject: values?.invitationSubject,
        invitationBody: values?.invitationBody,
        reminder: values?.isReminder
          ? {
              emailSubject: values?.emailSubject,
              emailBody: values?.emailBody,
            }
          : null,
      };

      let res: AxiosResponse = await http.patch(
        `/study/${studyId}/survey-package/${data?.id}`,
        body
      );
      toastMessage("success", res?.data?.message);
      closeModal();
      setSubmitLoader(false);
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  const handleTabChange = (_1: any, val: string) => {
    setType(val);
  };

  const tabIndicatorColor = (type: string, errors: any, touched: any) => {
    let indicatorColor = "primary.main";
    if (type === "invitation") {
      indicatorColor =
        (errors?.invitationSubject && touched?.invitationSubject) ||
        (errors?.invitationBody && touched?.invitationBody)
          ? "error.main"
          : "primary.main";
    } else if (type === "reminders") {
      indicatorColor =
        (errors?.emailSubject && touched?.emailSubject) ||
        (errors?.emailBody && touched?.emailBody)
          ? "error.main"
          : "primary.main";
    }
    return indicatorColor;
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <CustomModalHeader
          title={`Add Translations for ${data?.name}`}
          onCloseClick={closeModal}
        />
        <Divider sx={{ mt: 1 }} />
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            <CircularProgress size={25} />
          </Box>
        ) : (
          <Formik
            initialValues={{
              invitationSubject: langData?.invitationSubject || "",
              invitationBody: langData?.invitationBody || "",
              isReminder: data?.isReminder || false,
              emailSubject: langData?.surveyReminder?.emailSubject || "",
              emailBody: langData?.surveyReminder?.emailBody || "",
            }}
            validationSchema={schema}
            onSubmit={(values) => {
              submitHandler(values);
            }}
          >
            {({ getFieldProps, errors, touched, setFieldValue, values }) => (
              <Form style={DrawerForm}>
                <Box
                  sx={{ borderBottom: 1, mb: 1, borderBottomColor: "#E5E7EB" }}
                >
                  <StyledTabs
                    value={type}
                    onChange={handleTabChange}
                    sx={{
                      "& .MuiTabs-indicatorSpan": {
                        backgroundColor: tabIndicatorColor(
                          type,
                          errors,
                          touched
                        ),
                      },
                    }}
                  >
                    <StyledTab
                      label="Invitation"
                      value="invitation"
                      {...a11yProps(1)}
                      sx={{
                        color:
                          (errors?.invitationSubject &&
                            touched?.invitationSubject) ||
                          (errors?.invitationBody && touched?.invitationBody)
                            ? "error.main"
                            : "primary.main",
                        "&.Mui-selected": {
                          color:
                            (errors?.invitationSubject &&
                              touched?.invitationSubject) ||
                            (errors?.invitationBody && touched?.invitationBody)
                              ? "error.main"
                              : "primary.main",
                        },
                      }}
                    />
                    {data.isReminder && (
                      <StyledTab
                        label="Reminders"
                        value="reminders"
                        {...a11yProps(2)}
                        sx={{
                          color:
                            (errors?.emailSubject && touched?.emailSubject) ||
                            (errors?.emailBody && touched?.emailBody)
                              ? "error.main"
                              : "primary.main",
                          "&.Mui-selected": {
                            color:
                              (errors?.emailSubject && touched?.emailSubject) ||
                              (errors?.emailBody && touched?.emailBody)
                                ? "error.main"
                                : "primary.main",
                          },
                        }}
                      />
                    )}
                  </StyledTabs>
                </Box>
                <ModalTabPanel value={type} index={"invitation"}>
                  <InvitationTranslation
                    errors={errors}
                    touched={touched}
                    getFieldProps={getFieldProps}
                    setFieldValue={setFieldValue}
                    preview={preview}
                  />
                </ModalTabPanel>
                {data.isReminder && (
                  <ModalTabPanel value={type} index={"reminders"}>
                    <RemindersTranslation
                      errors={errors}
                      touched={touched}
                      getFieldProps={getFieldProps}
                      setFieldValue={setFieldValue}
                      values={values}
                      preview={preview}
                    />
                  </ModalTabPanel>
                )}
                <Box sx={{ mt: "auto" }}>
                  <Divider />
                  <Box sx={ModalDrawerButtonStyles}>
                    {!submitLoader ? (
                      <>
                        <Button onClick={closeModal} variant="outlined">
                          {preview ? "Close" : "Cancel"}
                        </Button>
                        {!preview && (
                          <Button type="submit" variant="contained">
                            {data?.id ? "Save" : "Add"}
                          </Button>
                        )}
                      </>
                    ) : (
                      <CircularProgress size={25} />
                    )}
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        )}
      </Box>
    </Modal>
  );
};

export default SurveyPackagTranslationModal;
